import type { MarketingTranslator } from '@/i18n/marketing'
/**
 * Content for the About Quebon page (`/about/quebon`).
 *
 * Marketing copy lives here, separated from JSX so revisions don't touch
 * components. Replace string values when finalised content arrives.
 */

type PhilosophyCardColor = 'red' | 'green' | 'blue' | 'yellow'

interface PhilosophyCard {
  chip: string
  title: string
  description: string
  color: PhilosophyCardColor
}

interface ProfileSection {
  label: string
  body: string[]
}

interface StoryTitleSegment {
  text: string
  highlight?: boolean
}

interface StoryBlock {
  titleSegments: StoryTitleSegment[]
  description: string
  closing: string
}

type PatentCountry = 'korea' | 'china' | 'US'

interface PatentItem {
  imgId: string
  country: PatentCountry
  title: string
}

interface FeaturedPatent {
  imgId: string
  title: string
  patentNumber: string
  imgBackUrl: string
}

interface MediaVideo {
  name: string
  shortLabel: string
  label: string
  thumbnail: string
  src: string
}

export function getAboutQuebonContent(mt: MarketingTranslator) {
  const aboutQuebonContent: {
    hero: {
      label: string
      subtitle: string
      title: string
      description: string
      descriptionHighlight: string
    }
    philosophy: {
      title: string
      cards: PhilosophyCard[]
    }
    story: {
      blocks: StoryBlock[]
      imageUrl: string
      imageUrlMobile: string
      imageAlt: string
    }
    founder: {
      label: string
      title: string
      sections: ProfileSection[]
      imageUrlXl: string
      imageUrlLg: string
      imageUrlMd: string
      imageUrlSm: string
      imageAlt: string
    }
    media: {
      title: string
      titleHighlight: string
      videos: MediaVideo[]
      moreLabel: string
      moreUrl: string
    }
    patents: {
      title: string
      titleHighlight: string
      description: string
      notice: string
      backgroundImageUrl: string
      cardBackImageUrl: string
      flagIconUrl: string
      cards: FeaturedPatent[]
    }
    technology: {
      title: string
      titleHighlight: string
      description: string
      patentNotice: string
      patents: PatentItem[]
    }
  } = {
    hero: {
      label: 'QUEBON',
      subtitle: mt('aboutQuebon.aboutQuebonContent.hero.subtitle'),
      title: mt('aboutQuebon.aboutQuebonContent.hero.title'),
      description:
        mt('aboutQuebon.aboutQuebonContent.hero.description'),
      descriptionHighlight: mt('aboutQuebon.aboutQuebonContent.hero.descriptionHighlight'),
    },

    philosophy: {
      title: mt('aboutQuebon.aboutQuebonContent.philosophy.title'),
      cards: [
        {
          chip: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.0.chip'),
          title: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.0.title'),
          description:
            mt('aboutQuebon.aboutQuebonContent.philosophy.cards.0.description'),
          color: 'red',
        },
        {
          chip: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.1.chip'),
          title: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.1.title'),
          description:
            mt('aboutQuebon.aboutQuebonContent.philosophy.cards.1.description'),
          color: 'green',
        },
        {
          chip: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.2.chip'),
          title: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.2.title'),
          description:
            mt('aboutQuebon.aboutQuebonContent.philosophy.cards.2.description'),
          color: 'blue',
        },
        {
          chip: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.3.chip'),
          title: mt('aboutQuebon.aboutQuebonContent.philosophy.cards.3.title'),
          description:
            mt('aboutQuebon.aboutQuebonContent.philosophy.cards.3.description'),
          color: 'yellow',
        },
      ],
    },

    story: {
      blocks: [
        {
          titleSegments: [
            { text: mt('aboutQuebon.aboutQuebonContent.story.blocks.0.titleSegments.0.text'), highlight: true },
            { text: mt('aboutQuebon.aboutQuebonContent.story.blocks.0.titleSegments.1.text') },
          ],
          description:
            mt('aboutQuebon.aboutQuebonContent.story.blocks.0.description'),
          closing: mt('aboutQuebon.aboutQuebonContent.story.blocks.0.closing'),
        },
        {
          titleSegments: [
            { text: 'True Pleasure' },
            { text: 'from True Learning', highlight: true },
          ],
          description:
            mt('aboutQuebon.aboutQuebonContent.story.blocks.1.description'),
          closing:
            mt('aboutQuebon.aboutQuebonContent.story.blocks.1.closing'),
        },
      ],
      imageUrl:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section02/02-img@2x.png',
      imageUrlMobile:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section02/02-img-md@2x.png',
      imageAlt: mt('aboutQuebon.aboutQuebonContent.story.imageAlt'),
    },

    founder: {
      label: mt('aboutQuebon.aboutQuebonContent.founder.label'),
      title: mt('aboutQuebon.aboutQuebonContent.founder.title'),
      sections: [
        {
          label: mt('aboutQuebon.aboutQuebonContent.founder.sections.0.label'),
          body: [
            mt('aboutQuebon.aboutQuebonContent.founder.sections.0.body.0'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.0.body.1'),
          ],
        },
        {
          label: mt('aboutQuebon.aboutQuebonContent.founder.sections.1.label'),
          body: [
            mt('aboutQuebon.aboutQuebonContent.founder.sections.1.body.0'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.1.body.1'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.1.body.2'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.1.body.3'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.1.body.4'),
          ],
        },
        {
          label: mt('aboutQuebon.aboutQuebonContent.founder.sections.2.label'),
          body: [
            mt('aboutQuebon.aboutQuebonContent.founder.sections.2.body.0'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.2.body.1'),
          ],
        },
        {
          label: mt('aboutQuebon.aboutQuebonContent.founder.sections.3.label'),
          body: [
            mt('aboutQuebon.aboutQuebonContent.founder.sections.3.body.0'),
            mt('aboutQuebon.aboutQuebonContent.founder.sections.3.body.1'),
          ],
        },
      ],
      imageUrlXl:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section03/03-img-sam-xl.png',
      imageUrlLg:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section03/03-img-sam-lg@2x.png',
      imageUrlMd:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section03/03-img-sam-md@2x.png',
      imageUrlSm:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section03/03-img-sam-sm@2x.png',
      imageAlt: mt('aboutQuebon.aboutQuebonContent.founder.imageAlt'),
    },

    media: {
      title: mt('aboutQuebon.aboutQuebonContent.media.title'),
      titleHighlight: mt('aboutQuebon.aboutQuebonContent.media.titleHighlight'),
      videos: [
        {
          name: 'kbs',
          shortLabel: 'KBS',
          label: mt('aboutQuebon.aboutQuebonContent.media.videos.0.label'),
          thumbnail:
            'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section04/img-kbs.png',
          src: 'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/broadcast-kbs.mp4/stream.m3u8',
        },
        {
          name: 'mbc',
          shortLabel: 'MBC',
          label: mt('aboutQuebon.aboutQuebonContent.media.videos.1.label'),
          thumbnail:
            'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section04/img-mbc.png',
          src: 'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/broadcast-mbc-short.mp4/stream.m3u8',
        },
        {
          name: 'sbs',
          shortLabel: 'SBS',
          label: mt('aboutQuebon.aboutQuebonContent.media.videos.2.label'),
          thumbnail:
            'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section04/img-sbs.png',
          src: 'https://quebon-public-media.s3.ap-northeast-2.amazonaws.com/broadcast-sbs.mp4/stream.m3u8',
        },
      ],
      moreLabel: mt('aboutQuebon.aboutQuebonContent.media.moreLabel'),
      moreUrl: 'https://www.quebon.tv/quebon-news/news',
    },

    patents: {
      title: mt('aboutQuebon.aboutQuebonContent.patents.title'),
      titleHighlight: mt('aboutQuebon.aboutQuebonContent.patents.titleHighlight'),
      description:
        mt('aboutQuebon.aboutQuebonContent.patents.description'),
      notice: mt('aboutQuebon.aboutQuebonContent.patents.notice'),
      backgroundImageUrl:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section05/img-background.png',
      cardBackImageUrl:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section05/img-card-bg.svg',
      flagIconUrl:
        'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section05/icon-flag.svg',
      cards: [
        {
          imgId: '10',
          title: mt('aboutQuebon.aboutQuebonContent.patents.cards.0.title'),
          patentNumber: '10-1833916',
          imgBackUrl:
            'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section05/img-back-1.png',
        },
        {
          imgId: '02',
          title: mt('aboutQuebon.aboutQuebonContent.patents.cards.1.title'),
          patentNumber: '10-2351377',
          imgBackUrl:
            'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section05/img-back-2.png',
        },
        {
          imgId: '11',
          title: mt('aboutQuebon.aboutQuebonContent.patents.cards.2.title'),
          patentNumber: '10-1859374',
          imgBackUrl:
            'https://d2ql3aoebfyj3b.cloudfront.net/quebon-introduction/section05/img-back-3.png',
        },
      ],
    },

    technology: {
      title: mt('aboutQuebon.aboutQuebonContent.technology.title'),
      titleHighlight: mt('aboutQuebon.aboutQuebonContent.technology.titleHighlight'),
      description:
        mt('aboutQuebon.aboutQuebonContent.technology.description'),
      patentNotice: mt('aboutQuebon.aboutQuebonContent.technology.patentNotice'),
      patents: [
        { imgId: '01', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.0.title') },
        { imgId: '02', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.patents.cards.1.title') },
        { imgId: '03', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.2.title') },
        { imgId: '04', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.3.title') },
        { imgId: '05', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.4.title') },
        { imgId: '06', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.5.title') },
        { imgId: '07', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.6.title') },
        { imgId: '08', country: 'china', title: mt('aboutQuebon.aboutQuebonContent.patents.cards.0.title') },
        { imgId: '09', country: 'US', title: mt('aboutQuebon.aboutQuebonContent.patents.cards.0.title') },
        { imgId: '10', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.patents.cards.0.title') },
        { imgId: '11', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.patents.cards.2.title') },
        { imgId: '12', country: 'US', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.11.title') },
        { imgId: '13', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.11.title') },
        { imgId: '14', country: 'korea', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.13.title') },
        { imgId: '15', country: 'US', title: mt('aboutQuebon.aboutQuebonContent.technology.patents.0.title') },
      ],
    },
  }
  return aboutQuebonContent
}
